import React, { useEffect, useRef, useState } from 'react';
import { getData, resolveImage } from '../../helpers/indexedDbHelper';
import { useRefresh } from '../../hooks/useRefresh';
import LayoutContent, { FOOTER_NOTICE } from '../../components/layouts/content2';
import { useTranslation } from 'react-i18next';
import ListProducts from './ListProducts';
import SpecialsScreen from './specials';
import CategoriesScreen from './categories';
import EventsScreen from './events';
import DetailProduct from './DetailProduct';
import { useSelector } from 'react-redux';
import Alcoholic from './alcoholic';
import Drinks from './drinks';
import LunchSpecial from './lunchspecial';
import CoffeShop from './coffeShop';
import Subcategories, { LangToggle } from './subcategories';
import { CATEGORY_BUTTONS, SUBCATEGORIES, findCategory } from './menuConfig';
import { isCategoryVisible, useNow } from '../../helpers/schedule';

import { isLocalMirror } from '../../config/constants';
const ACCENT = '#ED3E08';
const SLIDE_MS = 4500;

// Promos del welcome: se cargan solas desde ./assets/carrousel (orden alfabetico del archivo).
// Las imagenes duran SLIDE_MS; los videos pasan al siguiente cuando terminan.
// Solo se monta el video visible (varios videos a la vez congelaban los iPads).
const slidesContext = require.context('./assets/carrousel', false, /\.(png|jpe?g|webp|mp4)$/);
const SLIDES = slidesContext.keys().sort().map((key) => ({ src: slidesContext(key), video: key.endsWith('.mp4') }));

const WELCOME_CSS = `
@keyframes welcomePulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(237, 62, 8, 0.55), 0 0 18px 3px rgba(237, 62, 8, 0.45); }
    50% { box-shadow: 0 0 0 11px rgba(237, 62, 8, 0), 0 0 32px 7px rgba(237, 62, 8, 0.75); }
}
@keyframes welcomeShine {
    0% { transform: translateX(-120%) skewX(-20deg); }
    60%, 100% { transform: translateX(240%) skewX(-20deg); }
}
`;

function WelcomeCarousel({ onEnter, langEng }) {
    const [index, setIndex] = useState(0);
    const touchStart = useRef(null);
    const swiped = useRef(false);
    const [videoFailed, setVideoFailed] = useState(false);

    const next = () => setIndex((prev) => (prev + 1) % SLIDES.length);

    useEffect(() => {
        // Los videos avanzan al terminar; las imagenes (o un video que no carga) por tiempo
        if (SLIDES[index]?.video && !videoFailed) return undefined;
        const timer = setTimeout(next, SLIDE_MS);
        return () => clearTimeout(timer);
    }, [index, videoFailed]);

    useEffect(() => setVideoFailed(false), [index]);

    const goTo = (e, next) => {
        e.stopPropagation();
        setIndex(next);
    };

    const onTouchStart = (e) => {
        swiped.current = false;
        touchStart.current = e.touches[0].clientX;
    };

    const onTouchEnd = (e) => {
        if (touchStart.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStart.current;
        touchStart.current = null;

        if (Math.abs(delta) < 40) return;

        swiped.current = true;
        setIndex((prev) => (prev + (delta < 0 ? 1 : SLIDES.length - 1)) % SLIDES.length);
    };

    return (
        <div
            onClick={() => {
                if (swiped.current) {
                    swiped.current = false;
                    return;
                }
                onEnter();
            }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 99999,
                background: '#000',
                overflow: 'hidden',
                cursor: 'pointer',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}
        >
            <style>{WELCOME_CSS}</style>

            {/* Sliders a pantalla completa con cross-fade */}
            {SLIDES.map((slide, i) => (
                <div
                    key={i}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: i === index ? 1 : 0,
                        transition: 'opacity 900ms ease-in-out',
                        pointerEvents: 'none'
                    }}
                >
                    {slide.video ? (i === index && (
                        <video
                            src={slide.src}
                            autoPlay
                            muted
                            playsInline
                            disablePictureInPicture
                            loop={SLIDES.length === 1}
                            onEnded={next}
                            onError={() => setVideoFailed(true)}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                        />
                    )) : (
                        <img
                            src={slide.src}
                            alt={`promo-${i + 1}`}
                            onContextMenu={(e) => e.preventDefault()}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                display: 'block'
                            }}
                        />
                    )}
                </div>
            ))}

            {/* Degradado inferior para legibilidad del boton */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, transparent 35%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.9) 100%)',
                    pointerEvents: 'none'
                }}
            />

            {/* Indicadores + boton luminoso */}
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 26,
                    padding: '0 24px 8vh'
                }}
            >
                <div style={{ display: SLIDES.length > 1 ? 'flex' : 'none', gap: 10 }}>
                    {SLIDES.map((_, i) => (
                        <div
                            key={i}
                            onClick={(e) => goTo(e, i)}
                            style={{
                                width: i === index ? 30 : 10,
                                height: 10,
                                borderRadius: 6,
                                background: i === index ? ACCENT : 'rgba(255,255,255,0.45)',
                                boxShadow: i === index ? '0 0 12px 2px rgba(237,62,8,0.7)' : 'none',
                                transition: 'all 350ms ease',
                                cursor: 'pointer'
                            }}
                        />
                    ))}
                </div>

                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEnter();
                    }}
                    style={{
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 9,
                        padding: '12px 26px',
                        border: '1.5px solid rgba(255,255,255,0.35)',
                        borderRadius: 999,
                        background: `linear-gradient(135deg, ${ACCENT} 0%, #C70100 100%)`,
                        color: '#fff',
                        fontSize: 16,
                        fontWeight: 900,
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        animation: 'welcomePulseGlow 1.8s ease-in-out infinite',
                        WebkitTapHighlightColor: 'transparent'
                    }}
                >
                    <span style={{ position: 'relative', zIndex: 2 }}>
                        {langEng ? 'Tap to view menu' : 'Toca para ver el menú'}
                    </span>
                    <span style={{ position: 'relative', zIndex: 2, opacity: 0.6, fontWeight: 400 }}>/</span>
                    <span style={{ position: 'relative', zIndex: 2, fontSize: 13, opacity: 0.9 }}>
                        {langEng ? 'Toca para ver el menú' : 'Tap to view menu'}
                    </span>

                    {/* Brillo que recorre el boton */}
                    <span
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '35%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                            animation: 'welcomeShine 2.8s ease-in-out infinite',
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    />
                </button>
            </div>
        </div>
    );
}

const buttonImages = require.context('./assets/home/botones', false, /\.png$/);

// Redes del restaurante: al tocar el icono se muestra su QR
const SOCIALS = [
    { key: 'facebook', name: 'Facebook', icon: require('./assets/home/facebook.png'), qr: require('./assets/home/qr/facebook.png') },
    { key: 'instagram', name: 'Instagram', icon: require('./assets/home/instagram.png'), qr: require('./assets/home/qr/instagram.png') },
    { key: 'tiktok', name: 'TikTok', icon: require('./assets/home/tiktok.png'), qr: require('./assets/home/qr/tiktok.png') }
];

// Header del home: redes a la izquierda, logo al centro y selector EN/ES a la derecha
function HeaderBanner({ langEng, onSocial }) {
    const socialStyle = { height: 'clamp(30px, 4vw, 52px)', width: 'auto', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' };

    return (
        <div
            style={{
                background: '#000',
                borderRadius: '0 0 28px 28px',
                height: '15vh',
                minHeight: 120,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                padding: '0 5%'
            }}
        >
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '2.4vw' }}>
                {SOCIALS.map((social) => (
                    <img
                        key={social.key}
                        src={social.icon}
                        alt={social.name}
                        style={socialStyle}
                        onClick={() => onSocial(social)}
                    />
                ))}
            </div>

            <img
                src={require('./assets/home/logo.png')}
                alt="Habaneros"
                style={{ height: '82%', width: 'auto', objectFit: 'contain', pointerEvents: 'none' }}
            />

            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <LangToggle langEng={langEng} />
            </div>
        </div>
    );
}


function Index() {

    const {i18n} = useTranslation();
    const langEng = i18n.language === 'en' ?? false;
    const {change, getInitData} = useRefresh();
    const [menus, setMenus] = useState([]);
    const [social, setSocial] = useState(false);
    const [subcategory, setSubcategory] = useState(false);
    const now = useNow();
    const homeButtons = CATEGORY_BUTTONS.filter((button) => !button.category || isCategoryVisible({ name: button.category }, now));
    const [showWelcome, setShowWelcome] = useState(SLIDES.length > 0);
    const bannersRef = useRef(null);
    const [dataDish, setDataDish] = useState(false);
     const [showDetailProduct, setShowDetailProduct] = useState(false);
    const [showListProduct, setShowListProduct] = useState(false);
    const [showSpecials, setShowSpecials] = useState(false);
    const [showCocktails, setShowCocktails] = useState(false);
    const [showEvents, setShowEvents] = useState(false);
    const [showCategories, setshowCategories] = useState(false);
    const [dishs, setdishs] = useState(false);
    const [desserts, setdesserts] = useState(false);
    const [showDrinks, setShowDrinks] = useState(false);
    const [showLunchSpecial, setShowLunchSpecial] = useState(false);
    const [Beverages, setBeverages] = useState(false);
    const [cocina, setcolombiana] = useState(false);
    const [pescadomarico, setpescadomarico] = useState(false);
    const [fuertes, setfuertes] = useState(false);
    const [antojitos, setantojitos] = useState(false);
    const [desayuno, setdesayuno] = useState(false);
    const [picada, setpicada] = useState(false);
    const [entrada, setentrada] = useState(false);
    const [coffe, setcoffe] = useState(false);
    const [cocktail, setcocktail] = useState(false);
    const [pastas, setpastas] = useState(false);
    const [drinks, setdrinks] = useState(false);
    const [showCoffe, setshowCoffe] = useState(false);
    
   

    const fetchImage = async (id) => {
        return resolveImage(id);
    };


         const {
           goHomeInit
         } = useSelector((state) => state.initLoad);
        
    
        
    

    useEffect(() => {
        getData('menu').then((items) => {
            // Sin menu en disco todavia: no reventar la app entera.
            if (!items?.value?.data) return;
            const data = items.value.data;
           
            setMenus(data);
        });
    }, [change]);

    useEffect(() => {
        menus.map((item) => {
            if (item.id === 906) {
                setdesserts(item);
            }

            if (item.id === 897) {
                setcolombiana(item);
            }
            if (item.id === 900) {
                setpescadomarico(item);
            }

            if (item.id === 898) {
                setfuertes(item);
            }

            if (item.id === 895) {
                setantojitos(item);
            }

            if (item.id === 894) {
                setdesayuno(item);
            }

            if (item.id === 896) {
                setpicada(item);
            }

            if (item.id === 905) {
                setentrada(item);
            }
             if (item.id === 907) {
                setcoffe(item);
             }
            
            if (item.id === 902) {
                setcocktail(item);
            }
            if (item.id === 899) {
                setpastas(item);
            }
            if (item.id === 901) {
                setdrinks(item);
            }


            if (item.id === 902) {
                setBeverages(item)
            }



        });
    }, [menus]);


    const GoBeverages = () => {
        setShowListProduct(true);
        setdishs(Beverages);
    }

    const openCategory = (name) => {
        const category = findCategory(menus, name);
        if (!category) return;
        setdishs(category);
        setShowListProduct(true);
    }

    const onHomeButton = (button) => {
        if (button.sub) {
            setSubcategory(button.sub);
            return;
        }
        openCategory(button.category);
    }

    const bannerStyle = {
        width: '100%',
        height: 'auto',
        display: 'block',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent'
    };

    // Los videos no traen las esquinas redondeadas como los PNG: mismo radio (75px del arte original)
    const videoProps = { autoPlay: true, loop: true, muted: true, playsInline: true, disablePictureInPicture: true };
    const horizontalVideoStyle = { ...bannerStyle, aspectRatio: '1869 / 856', objectFit: 'cover', borderRadius: '4% / 8.76%' };
    const verticalVideoStyle = { ...bannerStyle, aspectRatio: '580 / 970', objectFit: 'cover', borderRadius: '12.93% / 7.73%' };


    
    // Los videos del home solo corren cuando el home se ve: tapado por el welcome,
    // la lista o las subcategorias se pausan para no saturar el iPad.
    const homeHidden = showWelcome || showListProduct || !!subcategory;
    useEffect(() => {
        bannersRef.current?.querySelectorAll('video').forEach((video) => {
            if (homeHidden) video.pause();
            else video.play().catch(() => {});
        });
    }, [homeHidden]);

    useEffect(() => {
        const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            console.log("La página ha perdido el foco (está oculta).");
            setShowListProduct(false);
            setShowSpecials(false)
          
          
           setShowLunchSpecial(false)
          
            setshowCategories(false)
            setShowDetailProduct(false)
            setShowCocktails(false)
            setShowEvents(false)
            setShowDrinks(false)
            setSubcategory(false)
            setShowWelcome(SLIDES.length > 0)
            i18n.changeLanguage('en')

            getInitData()
        } else if (document.visibilityState === "visible") {
            console.log("La página está en foco (visible).");
        }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Limpieza del evento al desmontar el componente
        return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);


      useEffect(() => {
     
                setShowListProduct(false);
            setShowSpecials(false)
          
           
           setShowLunchSpecial(false)
          
            setshowCategories(false)
          setShowDetailProduct(false)
           setShowCocktails(false)
            setShowEvents(false)
            setShowDrinks(false)
            setSubcategory(false)
         }, [goHomeInit]);

    window.onerror = function () {
        // En el kiosko NO se recarga a ciegas: el ErrorBoundary ya se recupera
        // solo, y recargar ante cualquier error suelto provocaba refrescos
        // sorpresa a los pocos segundos de abrir el menu.
        console.log('[Kiosko] Error global detectado');
        if (!isLocalMirror) {
            setTimeout(() => { window.location.reload(); }, 2000);
        }
        };

        
    

    return (
          <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999}}>
            {showWelcome && (
                <WelcomeCarousel
                    langEng={langEng}
                    onEnter={() => setShowWelcome(false)}
                />
            )}

            <LayoutContent goCategories={() => {
               setShowDrinks(true)
        }} showBack={false} showLogo={false} showLang={false} showFooter={false} showCocktails={ GoBeverages} showEvents={ setShowEvents} showImageTop={true} setShowSpecials={setShowSpecials}>
                <div
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ position: 'fixed', inset: 0, zIndex: 1, display: 'flex', background: '#f2f2f2' }}
                >
                    {/* Columna izquierda: header + banners */}
                    <div
                        ref={bannersRef}
                        style={{
                            width: '71%',
                            height: '100%',
                            overflowY: 'auto',
                            boxSizing: 'border-box',
                            padding: '0 2% 1.5vh',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: '2vh',
                            backgroundImage: `url(${require('./assets/home/background.png')})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    >
                        <HeaderBanner langEng={langEng} onSocial={setSocial} />

                        <video {...videoProps} src={require('./assets/home/banner-tbone.mp4')} style={horizontalVideoStyle} onClick={() => openCategory('STEAKS')} />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '3.5%' }}>
                            <video {...videoProps} src={require('./assets/home/banner-fajitas.mp4')} style={verticalVideoStyle} onClick={() => openCategory('FAJITAS')} />
                            <img src={require('./assets/home/banner-website.png')} alt="Visit our website" style={{ ...bannerStyle, cursor: 'default' }} />
                            <video {...videoProps} src={require('./assets/home/banner-molcajete.mp4')} style={verticalVideoStyle} onClick={() => setSubcategory('food')} />
                        </div>

                        <img src={require('./assets/home/banner-cocteles.png')} alt="Variedad de cócteles" style={bannerStyle} onClick={() => openCategory('SPECIALTY COCKTAILS')} />

                        <p
                            style={{
                                margin: 0,
                                textAlign: 'center',
                                fontFamily: 'Noodle',
                                fontSize: 'clamp(12px, 1.3vw, 17px)',
                                lineHeight: 1.15,
                                letterSpacing: 0.5,
                                color: '#000',
                                textTransform: 'uppercase'
                            }}
                        >
                            {langEng ? FOOTER_NOTICE.en : FOOTER_NOTICE.es}
                        </p>
                    </div>

                    {/* Columna derecha: botones de categorias */}
                    <div
                        style={{
                            width: '29%',
                            height: '100%',
                            boxSizing: 'border-box',
                            padding: '11vh 0 9vh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: '#111',
                            backgroundImage: `url(${require('./assets/home/background-categorias.png')})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    >
                        <img src={require('./assets/home/menu-title.png')} alt="Menu" style={{ width: '84%', height: 'auto', flexShrink: 0, pointerEvents: 'none' }} />

                        <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-evenly' }}>
                            {homeButtons.map((button) => (
                                <img
                                    key={button.key}
                                    src={buttonImages(`./${button.key}.png`)}
                                    alt={button.key}
                                    onClick={() => onHomeButton(button)}
                                    style={{
                                        width: '84%',
                                        maxHeight: `${Math.min(12.5, 90 / homeButtons.length)}%`,
                                        objectFit: 'contain',
                                        cursor: 'pointer',
                                        WebkitTapHighlightColor: 'transparent'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {social && (
                    <div
                        onClick={() => setSocial(false)}
                        style={{ position: 'fixed', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 9999 }}
                    >
                        <div style={{ background: '#fff', borderRadius: 28, padding: '3vh 3vw 3.5vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5vh' }}>
                            <img src={social.qr} alt={social.name} style={{ width: 'min(60vw, 420px)', height: 'auto', imageRendering: 'pixelated' }} />
                            <p style={{ margin: 0, fontFamily: "'Avigea', 'Times New Roman', serif", fontSize: 'clamp(24px, 3.4vw, 44px)', color: '#000' }}>
                                {social.name} · @habanerosutica
                            </p>
                            <p style={{ margin: 0, fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: 'clamp(14px, 1.8vw, 22px)', color: '#555' }}>
                                {langEng ? 'Scan with your phone camera' : 'Escanea con la cámara de tu celular'}
                            </p>
                        </div>
                    </div>
                )}

                {subcategory && <Subcategories
                    buttons={SUBCATEGORIES[subcategory].buttons}
                    images={SUBCATEGORIES[subcategory].images}
                    langEng={langEng}
                    onBack={() => setSubcategory(false)}
                    onSelect={(button) => openCategory(button.category)}
                />}

            {showListProduct && <ListProducts
                dishs={dishs}
                setShowListProduct={setShowListProduct}
            />}
            
            {showCategories && <CategoriesScreen setshowCategories={setshowCategories} />}
                {showSpecials && <SpecialsScreen setShowListProduct={setShowSpecials} />}

                {showLunchSpecial && <LunchSpecial setShowHappy={setShowLunchSpecial} />}
                
                {showCocktails && <Alcoholic setShowHappy={setShowCocktails} />}
                {showEvents && <EventsScreen setShowHappy={setShowEvents} />}  
                {showDrinks && <Drinks setShowHappy={setShowDrinks} />}  
                 {showCoffe && <CoffeShop setShowHappy={setshowCoffe} />}  
                 {showDetailProduct && <DetailProduct
                    dishMenu={dataDish}
                    setShowListProduct2={()=>setShowDetailProduct(false)}
                    setShowDetailProduct={setShowDetailProduct}
                />}
                

            </LayoutContent>
            </div>
    );
}

export default Index;